import { CandleType } from 'klinecharts'
import type { ChartTypeId, IndicatorOption } from './types'

/** Timeframe options (a curated subset of the datafeed's supported TIMEFRAMES). */
export const TIMEFRAME_OPTIONS = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1'] as const

/** TradingView-style display labels for the timeframe dropdown. */
export const TIMEFRAME_LABELS: Record<(typeof TIMEFRAME_OPTIONS)[number], string> = {
  M1: '1m',
  M5: '5m',
  M15: '15m',
  M30: '30m',
  H1: '1h',
  H4: '4h',
  D1: '1D',
  W1: '1W',
}

/**
 * The four chart shapes the picker offers. `engine` is the klinecharts candle
 * type; Heikin Ashi has no native type, so it renders as solid candles over an
 * HA-transformed series (see heikinAshi.ts).
 */
export interface ChartTypeDef {
  id: ChartTypeId
  label: string
  engine: CandleType
  ha: boolean
}

export const CHART_TYPES: ChartTypeDef[] = [
  { id: 'bar', label: 'Bars', engine: CandleType.Ohlc, ha: false },
  { id: 'candle', label: 'Candles', engine: CandleType.CandleSolid, ha: false },
  { id: 'heikin', label: 'Heikin Ashi', engine: CandleType.CandleSolid, ha: true },
  { id: 'line', label: 'Line', engine: CandleType.Area, ha: false },
]

export const chartTypeById = (id: string): ChartTypeDef =>
  CHART_TYPES.find((t) => t.id === id) ?? CHART_TYPES[1]

/** The special Volume entry — intercepted by the workbench, not a normal instance. */
export const VOLUME_OPTION: IndicatorOption = {
  key: 'VOLUME',
  name: 'VOLUME',
  label: 'Volume',
  pane: 'main',
  volume: true,
}

/**
 * All klinecharts v9 built-in indicators (plus the special Volume entry).
 * `pane: 'main'` overlays on the candles (price-series indicators); everything
 * else renders in its own sub-panel.
 */
export const INDICATOR_OPTIONS: IndicatorOption[] = [
  // Price overlays (main pane)
  { key: 'MA', name: 'MA', label: 'Moving Average', pane: 'main' },
  { key: 'EMA', name: 'EMA', label: 'Exponential Moving Average', pane: 'main' },
  { key: 'SMA', name: 'SMA', label: 'Smoothed Moving Average', pane: 'main' },
  { key: 'BBI', name: 'BBI', label: 'Bull and Bear Index', pane: 'main' },
  { key: 'BOLL', name: 'BOLL', label: 'Bollinger Bands', pane: 'main' },
  { key: 'SAR', name: 'SAR', label: 'Parabolic SAR', pane: 'main' },
  // Volume — special faded bars overlaid on the price pane
  VOLUME_OPTION,
  // Oscillators / volume studies (sub panel)
  { key: 'MACD', name: 'MACD', label: 'MACD', pane: 'sub' },
  { key: 'RSI', name: 'RSI', label: 'Relative Strength Index', pane: 'sub' },
  { key: 'KDJ', name: 'KDJ', label: 'KDJ Stochastic', pane: 'sub' },
  { key: 'CCI', name: 'CCI', label: 'Commodity Channel Index', pane: 'sub' },
  { key: 'DMI', name: 'DMI', label: 'Directional Movement Index', pane: 'sub' },
  { key: 'WR', name: 'WR', label: 'Williams %R', pane: 'sub' },
  { key: 'BIAS', name: 'BIAS', label: 'Bias Ratio', pane: 'sub' },
  { key: 'BRAR', name: 'BRAR', label: 'BRAR Sentiment', pane: 'sub' },
  { key: 'CR', name: 'CR', label: 'CR Energy', pane: 'sub' },
  { key: 'DMA', name: 'DMA', label: 'Difference of Moving Averages', pane: 'sub' },
  { key: 'TRIX', name: 'TRIX', label: 'Triple EMA Oscillator', pane: 'sub' },
  { key: 'OBV', name: 'OBV', label: 'On-Balance Volume', pane: 'sub' },
  { key: 'VR', name: 'VR', label: 'Volume Ratio', pane: 'sub' },
  { key: 'MTM', name: 'MTM', label: 'Momentum', pane: 'sub' },
  { key: 'EMV', name: 'EMV', label: 'Ease of Movement', pane: 'sub' },
  { key: 'PSY', name: 'PSY', label: 'Psychological Line', pane: 'sub' },
  { key: 'AO', name: 'AO', label: 'Awesome Oscillator', pane: 'sub' },
  { key: 'ROC', name: 'ROC', label: 'Rate of Change', pane: 'sub' },
  { key: 'PVT', name: 'PVT', label: 'Price Volume Trend', pane: 'sub' },
  { key: 'AVP', name: 'AVP', label: 'Average Price', pane: 'sub' },
]

export const indicatorByName = (name: string): IndicatorOption | undefined =>
  INDICATOR_OPTIONS.find((i) => i.name === name)
